import { DisclaimerModal, localStorageUtils, SDK_CONSTANTS } from 'fso-fincrime-sdk-ui';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';

import { IAIDisclaimerContainerProps } from '../../ts/entities/disclaimer/IAIDisclaimerContainerProps';
import { IExpirationTimeResult } from '../../ts/entities/disclaimer/IExpirationTimeResult';

/**
 * Component that handles displaying an AI disclaimer modal to users.
 * Shows a modal with configurable disclaimer text that requires user acknowledgment.
 * Stores acceptance status in a cookie to prevent repeated displays.
 *
 * Note: This component is only rendered when userInfo exists in the parent App component,
 * so there's no need to check for userInfo availability.
 *
 * @param {IAIDisclaimerContainerProps} props - Component props
 * @returns {JSX.Element | null} The disclaimer modal if conditions are met, null otherwise
 */
const AIDisclaimerContainer: React.FC<IAIDisclaimerContainerProps> = ({
  disclaimerCookieName = SDK_CONSTANTS.COOKIES.DISCLAIMER_ACK,
  requireProjectSettings = true,
  disclaimerConfigKey = 'REACT_APP_DISCLAIMER_TEXT',
}: IAIDisclaimerContainerProps): JSX.Element | null => {
  const { t } = useTranslation();
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const [cookies, setCookie] = useCookies([disclaimerCookieName]);

  /**
   * Calculate expiration time values based on years
   * @param yearsStr - String representation of years or default value
   * @returns Object containing expiration values in milliseconds and seconds
   */
  const calculateExpirationTime = useCallback((yearsStr?: string): IExpirationTimeResult => {
    const years = parseInt(yearsStr || '10', 10);
    const timeMs = years * 365 * 24 * 60 * 60 * 1000;
    const timeSec = years * 365 * 24 * 60 * 60;
    const date = new Date(Date.now() + timeMs);

    return {
      expirationTimeMs: timeMs,
      expirationTimeSec: timeSec,
      expirationDate: date,
    };
  }, []);

  /**
   * Retrieves disclaimer data from localStorage and handles expiration check
   * @param cookieName - The name of the cookie to check
   * @returns The localStorage disclaimer data or null if invalid/expired
   */
  const getLocalStorageDisclaimerData = useCallback((cookieName: string) => {
    try {
      const value = localStorageUtils.getSavedItem(cookieName);
      const expirationStr = localStorageUtils.getSavedItem(`${cookieName}_expiration`);

      if (value && expirationStr) {
        const expiration = parseInt(String(expirationStr), 10);
        if (!isNaN(expiration) && expiration > Date.now()) {
          return value;
        }
        localStorageUtils.removeSavedItem(cookieName);
        localStorageUtils.removeSavedItem(`${cookieName}_expiration`);
        return null;
      }
      return value;
    } catch (error) {
      console.error('Error accessing disclaimer data from localStorage:', error);
      return null;
    }
  }, []);

  /**
   * Set the disclaimer cookie with appropriate options
   * @param cookieName - The name of the cookie to set
   * @param value - The value to set (typically 'true')
   * @param setCookieFn - The setCookie function from useCookies hook
   * @param expirationTime - The expiration time values
   */
  const setDisclaimerCookie = useCallback(
    (
      cookieName: string,
      value: string,
      setCookieFn: (name: string, value: string, options?: any) => void,
      expirationTime: IExpirationTimeResult
    ) => {
      setCookieFn(cookieName, value, {
        path: '/',
        expires: expirationTime.expirationDate,
        sameSite: 'lax',
        secure: window.location.protocol === 'https:',
        maxAge: expirationTime.expirationTimeSec,
      });
    },
    []
  );

  /**
   * Persist disclaimer acceptance in localStorage for backup
   * @param cookieName - The name of the key in localStorage
   * @param value - The value to store (typically 'true')
   * @param expirationTimeMs - The expiration time in milliseconds
   */
  const persistToLocalStorage = useCallback(
    (cookieName: string, value: string, expirationTimeMs: number) => {
      try {
        const expirationDate = Date.now() + expirationTimeMs;
        localStorageUtils.setSavedItem(cookieName, value);
        localStorageUtils.setSavedItem(`${cookieName}_expiration`, expirationDate.toString());
      } catch (error) {
        console.error('Failed to set localStorage for disclaimer acceptance', error);
      }
    },
    []
  );

  const projectSettings = useMemo(() => {
    if (!requireProjectSettings) {
      return { isValid: true };
    }

    const projectName = localStorageUtils.getSavedItem(
      SDK_CONSTANTS.LOCAL_STORAGE_KEYS.PROJECT_SETTINGS.PROJECT_NAME
    );
    const tenant = localStorageUtils.getSavedItem(
      SDK_CONSTANTS.LOCAL_STORAGE_KEYS.PROJECT_SETTINGS.TENANT
    );

    return {
      projectName,
      tenant,
      isValid: !!projectName && !!tenant,
    };
  }, [requireProjectSettings]);

  /**
   * Check if disclaimer has been accepted via cookie or localStorage
   */
  const checkDisclaimerAccepted = useCallback(
    (cookieName: string) => {
      const cookieValue = cookies[cookieName];
      const localStorageData = getLocalStorageDisclaimerData(cookieName);

      return !!cookieValue || !!localStorageData;
    },
    [cookies, getLocalStorageDisclaimerData]
  );

  useEffect(() => {
    const shouldShow = projectSettings.isValid && !checkDisclaimerAccepted(disclaimerCookieName);

    if (shouldShow) {
      setDisclaimerOpen(true);
    }
  }, [projectSettings, cookies, disclaimerCookieName, checkDisclaimerAccepted]);

  /**
   * Get disclaimer text from the runtime config using the configKey
   */
  const disclaimerText = useMemo(() => {
    if (disclaimerConfigKey === 'REACT_APP_DISCLAIMER_TEXT') {
      return window.__RUNTIME_CONFIG__?.REACT_APP_DISCLAIMER_TEXT ?? '';
    }
    return (
      window.__RUNTIME_CONFIG__?.[disclaimerConfigKey as keyof typeof window.__RUNTIME_CONFIG__] ??
      ''
    );
  }, [disclaimerConfigKey]);

  /**
   * Handles the acceptance of the disclaimer modal.
   * Sets the acceptance cookie and persists the state in localStorage.
   * Closes the disclaimer modal.
   *
   * @returns {void}
   */
  const handleDisclaimerOk = useCallback((): void => {
    // Get the cookie expiration from runtime config or use default of 10 years
    const expirationYearsStr =
      window.__RUNTIME_CONFIG__?.REACT_APP_DISCLAIMER_COOKIE_EXPIRATION_YEARS ?? '10';
    const expirationTimes = calculateExpirationTime(expirationYearsStr);
    setDisclaimerCookie(disclaimerCookieName, 'true', setCookie, expirationTimes);
    persistToLocalStorage(disclaimerCookieName, 'true', expirationTimes.expirationTimeMs);
    setDisclaimerOpen(false);
  }, [
    calculateExpirationTime,
    setDisclaimerCookie,
    persistToLocalStorage,
    disclaimerCookieName,
    setCookie,
  ]);

  if (!disclaimerText) {
    return null;
  }

  return (
    <DisclaimerModal
      title={t('sdk-ui-translations:DISCLAIMER_POPPER.TITLE')}
      disclaimerText={disclaimerText}
      buttonText={t('sdk-ui-translations:DISCLAIMER_POPPER.OK')}
      isOpen={disclaimerOpen}
      onAccept={handleDisclaimerOk}
    />
  );
};

export default AIDisclaimerContainer;
